import {useCallback, useMemo} from 'react';

import {usePolicyAgencyCodesTreeQuery} from 'modules/decisionsNext/api/trees/policyAgencyCodesTree.generated';
import {GenericTreeNode, groupSelectedKeys, ungroupSelectedKeys, iterateTree} from 'utils/treeUtils';
import {PolicyAgencyCodesFilterKeys} from 'core/constants/policyFilter';

import {SkipQueryParams, StoreAdapterParams, TreeBasicNode} from 'components/Tree/TreeTypes';
import {useOptimizedSelection} from 'components/Tree/hooks/useOptimizedSelection';

type PolicyAgencyTreeNode = GenericTreeNode<
  TreeBasicNode & {
    __typename?: 'AgencyCodeCategory' | 'AgencyCode';
  }
>;

export const usePolicyAgencyCodesTree = ({skip}: SkipQueryParams = {}) => {
  const {data, loading} = usePolicyAgencyCodesTreeQuery({
    skip,
    disableSkipSetsLoadingState: true,
  });
  const treeData = useMemo<PolicyAgencyTreeNode[]>(() => data?.sourceCodes.policyAgencyCodes ?? [], [data]);

  return {
    treeData,
    loading,
    treeMap: useMemo(() => {
      const map = new Map<string, PolicyAgencyTreeNode>();

      iterateTree(
        treeData,
        (node, parent) => {
          map.set(node.key, {
            key: node.key,
            title: node.title,
            parentKey: parent?.key,
          });
        },
        {withParent: true}
      );

      return map;
    }, [treeData]),
  };
};

export const usePolicyAgencyCodesTreeSelection = ({
  store,
  setStore,
  skip,
}: StoreAdapterParams<PolicyAgencyCodesFilterKeys> & SkipQueryParams) => {
  const {treeData, loading} = usePolicyAgencyCodesTree({skip});

  const {policyAgencyCodes} = store;
  const selected = useMemo(
    () => Array.from(ungroupSelectedKeys(treeData, policyAgencyCodes ?? [])),
    [treeData, policyAgencyCodes]
  );

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const groupedSelected = Array.from(groupSelectedKeys(treeData, newSelected));
      setStore({
        policyAgencyCodes: groupedSelected.length ? groupedSelected : undefined,
      });
    },
    [setStore, treeData]
  );

  const setSelectedNone = useCallback(() => {
    setStore({
      policyAgencyCodes: undefined,
    });
  }, [setStore]);

  return {
    ...useOptimizedSelection({
      selected,
      setSelected,
      setSelectedNone,
      treeData,
      groupSelection: true,
    }),
    selectedNoneData: {
      policyAgencyCodes: undefined,
    },
    treeData,
    isLoading: loading,
  };
};
